/**
 * Django REST API Client — aligned with Configurator/lib/django-api.ts
 * Handles all communication with the Django backend
 * Base URL: https://proxyprintery.de
 */

export type CardConfig = any;

// Django API base URL
const DJANGO_API_URL = process.env.REACT_APP_DJANGO_API_URL || 'https://proxyprintery.de';

/**
 * User data structure from Django API
 */
export interface DjangoUser {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  phone: string
  default_address_line1: string
  default_address_line2: string
  default_city: string
  default_state: string
  default_postal_code: string
  default_country: string
  discord_username: string
  discord_avatar: string
  oauth_provider: string
  has_discord: boolean
  created_at: string
  updated_at: string
}

/**
 * Login response structure
 */
export interface LoginResponse {
  user: DjangoUser
  access: string
  refresh: string
}

/**
 * Register response structure
 */
export interface RegisterResponse {
  user: DjangoUser
  access: string
  refresh: string
}

/**
 * Get auth token from storage
 */
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
}

/**
 * Set auth token in storage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

/**
 * Remove auth token from storage
 */
export function removeAuthToken(): void {
  localStorage.removeItem('auth_token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Make authenticated request to Django API with automatic token refresh
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  let response = await fetch(`${DJANGO_API_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  // If we get a 401 and have a refresh token, try to refresh and retry
  if (response.status === 401 && typeof window !== 'undefined') {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (refreshToken) {
      try {
        // Try to refresh the token
        const refreshResponse = await fetch(`${DJANGO_API_URL}/api/auth/token/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setAuthToken(data.access);
          
          // Retry the original request with the new token
          headers['Authorization'] = `Bearer ${data.access}`;
          response = await fetch(`${DJANGO_API_URL}${endpoint}`, {
            ...options,
            headers,
          });
        } else {
          // Refresh token is also invalid, clear everything
          removeAuthToken();
          localStorage.removeItem('refresh_token');
        }
      } catch (error) {
        console.error('[API] Token refresh failed:', error);
        removeAuthToken();
        localStorage.removeItem('refresh_token');
      }
    }
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || error.error || error.message || `HTTP ${response.status}`);
  }
  
  // Handle empty responses (like DELETE 204 No Content)
  const contentType = response.headers.get('content-type');
  if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
    return undefined as T;
  }
  
  return response.json();
}

/**
 * Authentication API
 */
export const authAPI = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || error.detail || 'Invalid credentials');
    }
    
    const data: LoginResponse = await response.json();
    setAuthToken(data.access);
    if (typeof window !== 'undefined') {
      localStorage.setItem('refresh_token', data.refresh);
    }
    return data;
  },
  
  async register(
    username: string, 
    email: string, 
    password: string,
    firstName?: string,
    lastName?: string,
    phone?: string
  ): Promise<RegisterResponse> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        username, 
        email, 
        password,
        first_name: firstName,
        last_name: lastName,
        phone,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' }));
      if (error.email) {
        throw new Error(error.email[0] || 'Email error');
      }
      if (error.username) {
        throw new Error(error.username[0] || 'Username error');
      }
      if (error.password) {
        throw new Error(error.password[0] || 'Password error');
      }
      throw new Error(error.error || error.detail || 'Registration failed');
    }
    
    const data: RegisterResponse = await response.json();
    setAuthToken(data.access);
    if (typeof window !== 'undefined') {
      localStorage.setItem('refresh_token', data.refresh);
    }
    return data;
  },
  
  async discordLogin(code: string, redirectUri: string): Promise<LoginResponse> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/discord/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Discord login failed' }));
      throw new Error(error.error || error.detail || 'Discord authentication failed');
    }
    
    const data: LoginResponse = await response.json();
    setAuthToken(data.access);
    if (typeof window !== 'undefined') {
      localStorage.setItem('refresh_token', data.refresh);
    }
    return data;
  },
  
  async getCurrentUser(): Promise<DjangoUser> {
    return fetchAPI<DjangoUser>('/api/auth/me/');
  },
  
  async updateUser(updates: Partial<{
    first_name: string
    last_name: string
    phone: string
    default_address_line1: string
    default_address_line2: string
    default_city: string
    default_state: string
    default_postal_code: string
    default_country: string
  }>): Promise<DjangoUser> {
    return fetchAPI<DjangoUser>('/api/auth/me/', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  
  async refreshToken(refreshToken: string): Promise<{ access: string }> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Token refresh failed' }));
      throw new Error(error.detail || 'Token is invalid or expired');
    }
    
    const data = await response.json();
    setAuthToken(data.access);
    return data;
  },
  
  async logout(): Promise<void> {
    try {
      await fetchAPI('/api/auth/logout/', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Server logout failed:', error);
    } finally {
      removeAuthToken();
      if (typeof window !== 'undefined') {
        localStorage.removeItem('refresh_token');
      }
    }
  },
  
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/forgot-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to send password reset email');
    }
    
    return response.json();
  },
  
  async resetPassword(
    uid: string, 
    token: string, 
    newPassword: string, 
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await fetch(`${DJANGO_API_URL}/api/auth/reset-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        uid, 
        token, 
        new_password: newPassword, 
        confirm_password: confirmPassword 
      }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Password reset failed' }));
      throw new Error(error.error || error.detail || 'Password reset failed');
    }
    
    return response.json();
  },
  
  async changePassword(
    oldPassword: string, 
    newPassword: string, 
    confirmPassword: string
  ): Promise<{ message: string }> {
    return fetchAPI('/api/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify({ 
        old_password: oldPassword, 
        new_password: newPassword, 
        confirm_password: confirmPassword 
      }),
    });
  },
};

/**
 * Configurator Card Response Types
 */
export interface ConfiguratorCard {
  id: string
  sku: string
  slug: string
  title: string
  description: string
  card_type: string
  frame_version: string
  card_config: CardConfig
  
  card_name: string
  mana_cost: string
  type_line: string
  rules_text: string
  power: string
  toughness: string
  loyalty: string
  artist: string
  set_code: string
  collector_number: string
  rarity: string
  
  thumbnail_small: string
  thumbnail_medium: string
  high_res_url: string
  
  is_public: boolean
  nsfw: boolean
  moderation_status: string
  rejection_reason: string
  
  version: number
  is_remix: boolean
  parent_card?: string
  original_creator?: string
  
  like_count: number
  view_count: number
  remix_count: number
  use_count: number
  
  tags: string[]
  
  creator: string
  is_liked: boolean
  can_edit: boolean
  
  created_at: string
  updated_at: string
}

export interface ConfiguratorCardListResponse {
  count: number
  next: string | null
  previous: string | null
  results: ConfiguratorCard[]
}

/**
 * Cards API - Configurator Card Endpoints
 */
export const cardsAPI = {
  async create(params: {
    title: string
    cardType: string
    frameVersion: string
    cardConfig: CardConfig
    highResImage: Blob | File
    description?: string
    thumbnailSmall?: Blob | File
    thumbnailMedium?: Blob | File
    isPublic?: boolean
    nsfw?: boolean
    tags?: string[]
  }): Promise<ConfiguratorCard> {
    const formData = new FormData();
    
    formData.append('title', params.title);
    formData.append('card_type', params.cardType);
    formData.append('frame_version', params.frameVersion);
    formData.append('card_config', JSON.stringify(params.cardConfig));
    formData.append('high_res_image', params.highResImage);
    
    if (params.description) formData.append('description', params.description);
    if (params.thumbnailSmall) formData.append('thumbnail_small', params.thumbnailSmall);
    if (params.thumbnailMedium) formData.append('thumbnail_medium', params.thumbnailMedium);
    if (params.isPublic !== undefined) formData.append('is_public', String(params.isPublic));
    if (params.nsfw !== undefined) formData.append('nsfw', String(params.nsfw));
    if (params.tags) formData.append('tags', JSON.stringify(params.tags));
    
    const token = getAuthToken();
    const response = await fetch(`${DJANGO_API_URL}/api/cards/configurator/`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || JSON.stringify(error));
    }
    
    return response.json();
  },
  
  async list(params: {
    page?: number
    pageSize?: number
    search?: string
  } = {}): Promise<ConfiguratorCardListResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.pageSize) queryParams.set('page_size', params.pageSize.toString());
    if (params.search) queryParams.set('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI<ConfiguratorCardListResponse>(`/api/cards/configurator/${query ? `?${query}` : ''}`);
  },
  
  async myCards(params: {
    page?: number
    pageSize?: number
    search?: string
  } = {}): Promise<ConfiguratorCardListResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.pageSize) queryParams.set('page_size', params.pageSize.toString());
    if (params.search) queryParams.set('search', params.search);
    
    const query = queryParams.toString();
    return fetchAPI<ConfiguratorCardListResponse>(`/api/cards/configurator/my_cards/${query ? `?${query}` : ''}`);
  },
  
  async get(idOrSlug: string): Promise<ConfiguratorCard> {
    return fetchAPI<ConfiguratorCard>(`/api/cards/configurator/${idOrSlug}/`);
  },
  
  async getBySlug(slug: string): Promise<ConfiguratorCard> {
    return this.get(slug);
  },
  
  async getSimilar(idOrSlug: string): Promise<{
    reference_card: {
      id: string
      slug: string
      title: string
      card_type: string
      cmc: number
      colors: string[]
    }
    similar_cards: ConfiguratorCard[]
    count: number
  }> {
    return fetchAPI(`/api/cards/configurator/${idOrSlug}/similar/`);
  },
  
  async update(cardId: string, params: {
    title?: string
    description?: string
    cardConfig?: CardConfig
    highResImage?: Blob | File
    thumbnailSmall?: Blob | File
    thumbnailMedium?: Blob | File
    tags?: string[]
    nsfw?: boolean
  }): Promise<ConfiguratorCard> {
    const formData = new FormData();
    
    if (params.title) formData.append('title', params.title);
    if (params.description) formData.append('description', params.description);
    if (params.cardConfig) formData.append('card_config', JSON.stringify(params.cardConfig));
    if (params.highResImage) formData.append('high_res_image', params.highResImage);
    if (params.thumbnailSmall) formData.append('thumbnail_small', params.thumbnailSmall);
    if (params.thumbnailMedium) formData.append('thumbnail_medium', params.thumbnailMedium);
    if (params.tags) formData.append('tags', JSON.stringify(params.tags));
    if (params.nsfw !== undefined) formData.append('nsfw', String(params.nsfw));
    
    const token = getAuthToken();
    const response = await fetch(`${DJANGO_API_URL}/api/cards/configurator/${cardId}/`, {
      method: 'PATCH',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Update failed' }));
      throw new Error(error.detail || JSON.stringify(error));
    }
    
    return response.json();
  },
  
  async delete(cardId: string): Promise<void> {
    await fetchAPI(`/api/cards/configurator/${cardId}/`, {
      method: 'DELETE',
    });
  },
  
  async gallery(params: {
    sort?: 'recent' | 'popular' | 'remixed'
    cardType?: string
    search?: string
    cmc?: string | number
    colors?: string
    colorMatch?: 'includes' | 'exact' | 'identity'
    page?: number
    pageSize?: number
  } = {}): Promise<ConfiguratorCardListResponse> {
    const queryParams = new URLSearchParams();
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.cardType) queryParams.set('card_type', params.cardType);
    if (params.search) queryParams.set('search', params.search);
    if (params.cmc !== undefined) queryParams.set('cmc', params.cmc.toString());
    if (params.colors) queryParams.set('colors', params.colors);
    if (params.colorMatch) queryParams.set('color_match', params.colorMatch);
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.pageSize) queryParams.set('page_size', params.pageSize.toString());
    
    const query = queryParams.toString();
    return fetchAPI<ConfiguratorCardListResponse>(`/api/cards/configurator/gallery/${query ? `?${query}` : ''}`);
  },
  
  async like(cardId: string): Promise<{ message: string }> {
    return fetchAPI(`/api/cards/configurator/${cardId}/like/`, {
      method: 'POST',
    });
  },
  
  async unlike(cardId: string): Promise<void> {
    await fetchAPI(`/api/cards/configurator/${cardId}/like/`, {
      method: 'DELETE',
    });
  },
  
  async remix(cardId: string, params: {
    title: string
    cardConfig: CardConfig
    highResImage: Blob | File
    description?: string
    thumbnailSmall?: Blob | File
    thumbnailMedium?: Blob | File
    tags?: string[]
  }): Promise<ConfiguratorCard> {
    const formData = new FormData();
    
    formData.append('title', params.title);
    formData.append('card_config', JSON.stringify(params.cardConfig));
    formData.append('high_res_image', params.highResImage);
    
    if (params.description) formData.append('description', params.description);
    if (params.thumbnailSmall) formData.append('thumbnail_small', params.thumbnailSmall);
    if (params.thumbnailMedium) formData.append('thumbnail_medium', params.thumbnailMedium);
    if (params.tags) formData.append('tags', JSON.stringify(params.tags));
    
    const token = getAuthToken();
    const response = await fetch(`${DJANGO_API_URL}/api/cards/configurator/${cardId}/remix/`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Remix failed' }));
      throw new Error(error.detail || JSON.stringify(error));
    }
    
    return response.json();
  },
};

/**
 * Project Types
 */
export interface MinimalProject {
  id: string
  name: string
  source: 'APP' | 'CONFIGURATOR'
  card_count: number
  created_at: string
  updated_at: string
}

export interface Project {
  id: string
  name: string
  description: string
  source: 'APP' | 'CONFIGURATOR'
  default_cardback_id: string | null
  cardstock: string
  language: string
  is_archived: boolean
  project_type: string
  booster_pack_type: string | null
  is_locked: boolean
  max_cards: number | null
  is_booster_pack: boolean
  cards: ProjectCard[]
  card_count: number
  created_at: string
  updated_at: string
}

export interface ProjectCard {
  id: number
  card_identifier: string
  card_source: string
  card_version: string
  slot: number
  face: 'FRONT' | 'BACK'
  search_query: string
  quantity: number
  custom_cardback_id: string | null
  paper_type: string | null
  card_data: any
  created_at: string
  updated_at: string
}

export interface UploadAndAddToProjectResponse {
  card: ConfiguratorCard
  project_card: ProjectCard
  message: string
}

export interface PaperType {
  code: string
  name: string
  description: string
  price_per_card: string
  currency: string
  display_order: number
  pricing_note: string
}

export interface Extra {
  id: string
  sku: string
  name: string
  description: string
  type: string
  type_name: string
  type_code: string
  price: string
  currency: string
  link: string
  images: Array<{
    id: string
    url: string
    is_primary: boolean
    display_order: number
  }>
  display_order: number
  has_variants: boolean
  paper_options: Array<{
    extra_id: string
    sku: string
    paper_type: string
    paper_type_display: string
    price: string
    card_count: number
  }>
}

export interface Gift {
  id: string
  sku: string
  name: string
  description: string
  value: string
  image_url: string
  display_order: number
}

export interface PricePreview {
  total_quantity: number
  paper_type_breakdown: Array<{
    paper_type: string
    paper_type_name?: string
    quantity: number
    unit_price: string
    total: string
  }>
  cards_subtotal: string
  extras_breakdown: Array<{
    extra_id: string
    name: string
    quantity: number
    unit_price: string
    total: string
  }>
  extras_subtotal: string
  projects_breakdown: Array<{
    project_id: string
    project_name: string
    project_type: string
    booster_pack_type: string | null
    is_booster_pack: boolean
    is_locked: boolean
    card_count: number
    packaging_fee: string
  }>
  booster_packs_count: number
  packaging_fees: string
  subtotal: string
  coupon?: {
    code: string
    discount_type: string
    discount_value: string
    discount_amount: string
    applied_to?: string
  } | null
  coupon_discount: string
  total_before_coupon: string
  shipping_cost: string
  shipping_method: string
  free_shipping_threshold?: string
  free_shipping_eligible?: boolean
  tax: string
  total: string
  currency: string
  gift_balance: string
  available_gifts?: Array<{
    id: string
    name: string
    value: string
    can_afford: boolean
  }>
}

export interface CouponValidation {
  valid: boolean
  coupon?: {
    code: string
    discount_type: string
    discount_value: string
    description: string
    scope: string
    min_order_amount: string
    valid_from: string
    valid_until: string
  }
  error?: string
}

export interface OrderItem {
  id: number
  card_identifier: string
  card_name: string
  card_image_url: string
  cardback_identifier: string
  cardback_name: string
  cardback_image_url: string
  paper_type: string
  paper_type_name: string
  quantity: number
  unit_price: string
  total_price: string
}

export interface OrderProject {
  id: number
  project_name: string
  packaging_fee?: string
  subtotal: string
  display_order: number
  item_count: number
  items: OrderItem[]
}

export interface OrderExtraItem {
  id: number
  extra_name: string
  extra_type: string
  quantity: number
  unit_price: string
  total_price: string
}

export interface OrderGiftItem {
  id: number
  gift_name: string
  gift_value: string
}

export interface Order {
  id: string
  order_number: string
  status: string
  project_count: number
  cards_subtotal: string
  extras_subtotal: string
  subtotal: string
  coupon_code?: string | null
  coupon_discount?: string
  gift_balance?: string
  gift_balance_used?: string
  gift_balance_remaining?: string
  shipping_cost: string
  tax: string
  total: string
  currency: string
  shipping_name: string
  shipping_address_line1: string
  shipping_address_line2?: string
  shipping_city: string
  shipping_state?: string
  shipping_postal_code?: string
  shipping_country: string
  shipping_method: string
  tracking_number?: string
  tracking_url?: string
  payment_method: string
  payment_id?: string
  paid_at: string | null
  customer_notes?: string
  order_projects?: OrderProject[]
  items?: OrderItem[]
  order_extras?: OrderExtraItem[]
  order_gifts?: OrderGiftItem[]
  item_count: number
  created_at: string
  updated_at?: string
  shipped_at?: string | null
  delivered_at?: string | null
}

/**
 * Projects API - Always filters by source: "CONFIGURATOR"
 */
export const projectsAPI = {
  async getMinimalList(): Promise<MinimalProject[]> {
    return fetchAPI<MinimalProject[]>('/api/projects/minimal-list/?source=CONFIGURATOR');
  },
  
  async getList(): Promise<{ count: number, results: Project[] }> {
    return fetchAPI<{ count: number, results: Project[] }>('/api/projects/?source=CONFIGURATOR');
  },
  
  async create(params: {
    name: string
    description?: string
    cardstock?: string
    default_cardback_id?: string
    language?: string
  }): Promise<Project> {
    return fetchAPI<Project>('/api/projects/', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        source: 'CONFIGURATOR',
        project_type: 'STANDARD',
        language: params.language || 'en',
        is_archived: false,
      }),
    });
  },
  
  async get(id: string): Promise<Project> {
    return fetchAPI<Project>(`/api/projects/${id}/`);
  },
  
  async update(id: string, params: Partial<{
    name: string
    description: string
    cardstock: string
    default_cardback_id: string
    is_archived: boolean
  }>): Promise<Project> {
    return fetchAPI<Project>(`/api/projects/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  },
  
  async delete(id: string): Promise<void> {
    await fetchAPI(`/api/projects/${id}/`, {
      method: 'DELETE',
    });
  },
  
  async duplicate(id: string): Promise<Project> {
    return fetchAPI<Project>(`/api/projects/${id}/duplicate/`, {
      method: 'POST',
    });
  },
  
  async addCard(projectId: string, params: {
    card_identifier: string
    card_version: string
    quantity?: number
    face?: 'FRONT' | 'BACK'
    custom_cardback_id?: string
    paper_type?: string
  }): Promise<ProjectCard> {
    return fetchAPI<ProjectCard>(`/api/projects/${projectId}/cards/`, {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        card_source: 'CONFIGURATOR',
        quantity: params.quantity || 1,
        face: params.face || 'FRONT',
      }),
    });
  },
  
  async updateCard(projectId: string, cardId: number, params: {
    quantity?: number
    custom_cardback_id?: string
    paper_type?: string
  }): Promise<ProjectCard> {
    return fetchAPI<ProjectCard>(`/api/projects/${projectId}/cards/${cardId}/`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  },
  
  async deleteCard(projectId: string, cardId: number): Promise<void> {
    await fetchAPI(`/api/projects/${projectId}/cards/${cardId}/`, {
      method: 'DELETE',
    });
  },
  
  async clearCards(projectId: string): Promise<{ message: string, deleted_count: number }> {
    return fetchAPI<{ message: string, deleted_count: number }>(`/api/projects/${projectId}/clear/`, {
      method: 'POST',
    });
  },
  
  async uploadAndAddToProject(params: {
    title: string
    cardType: string
    frameVersion: string
    cardConfig: CardConfig
    highResImage: Blob | File
    projectId: string
    description?: string
    isPublic?: boolean
    nsfw?: boolean
    tags?: string[]
    quantity?: number
    face?: 'FRONT' | 'BACK'
    slot?: number
  }): Promise<UploadAndAddToProjectResponse> {
    const formData = new FormData();
    
    formData.append('title', params.title);
    formData.append('card_type', params.cardType);
    formData.append('frame_version', params.frameVersion);
    formData.append('card_config', JSON.stringify(params.cardConfig));
    formData.append('high_res_image', params.highResImage);
    formData.append('project_id', params.projectId);
    
    if (params.description) formData.append('description', params.description);
    if (params.isPublic !== undefined) formData.append('is_public', String(params.isPublic));
    if (params.nsfw !== undefined) formData.append('nsfw', String(params.nsfw));
    if (params.tags) formData.append('tags', JSON.stringify(params.tags));
    if (params.quantity !== undefined) formData.append('quantity', params.quantity.toString());
    if (params.face) formData.append('face', params.face);
    if (params.slot !== undefined) formData.append('slot', params.slot.toString());
    
    const token = getAuthToken();
    const response = await fetch(`${DJANGO_API_URL}/api/cards/configurator/upload-and-add-to-project/`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || JSON.stringify(error));
    }
    
    return response.json();
  },
};

/**
 * Cardback Types
 */
export interface Cardback {
  id: string
  name: string
  description: string
  thumbnail_small: string
  thumbnail_medium: string
  tags: string[]
  uploader: string
  visibility: 'PUBLIC' | 'PRIVATE'
  moderation_status: 'PENDING' | 'APPROVED' | 'REJECTED'
  like_count: number
  use_count: number
  created_at: string
  updated_at: string
}

export interface CardbacksResponse {
  count: number
  cardbacks: Cardback[]
}

/**
 * Cardbacks API
 */
export const cardbacksAPI = {
  async getCardbacks(): Promise<Cardback[]> {
    const response = await fetchAPI<CardbacksResponse>('/api/cards/cardbacks/');
    return response.cardbacks;
  },
  
  async setCardback(projectId: string, cardId: number, cardbackId: string): Promise<ProjectCard> {
    return fetchAPI<ProjectCard>(`/api/projects/${projectId}/cards/${cardId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ custom_cardback_id: cardbackId }),
    });
  },
};

/**
 * Orders API
 */
export const ordersAPI = {
  async getPricing(params: {
    cards?: Array<{ paper_type: string, quantity: number }>
    shipping_method?: string
    country?: string
    coupon_code?: string
    extras?: Array<{ extra_id: string, quantity: number }>
    project_ids?: string[]
  }): Promise<PricePreview> {
    return fetchAPI<PricePreview>('/api/orders/pricing/', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        shipping_method: params.shipping_method || 'STANDARD',
        country: params.country || 'US',
      }),
    });
  },

  async getPricePreview(params: {
    project_ids: string[]
    shipping_method?: string
    country?: string
    coupon_code?: string
    extras?: Array<{ extra_id: string, quantity: number }>
  }): Promise<PricePreview> {
    return fetchAPI<PricePreview>('/api/orders/price-preview/', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        shipping_method: params.shipping_method || 'STANDARD',
        country: params.country || 'US',
      }),
    });
  },
  
  async validateCoupon(code: string): Promise<CouponValidation> {
    return fetchAPI<CouponValidation>('/api/orders/validate-coupon/', {
      method: 'POST',
      body: JSON.stringify({ coupon_code: code }),
    });
  },
  
  async getPaperTypes(): Promise<PaperType[]> {
    return fetchAPI<PaperType[]>('/api/orders/paper-types/');
  },
  
  async getExtras(): Promise<Extra[]> {
    return fetchAPI<Extra[]>('/api/orders/extras/');
  },
  
  async getGifts(): Promise<Gift[]> {
    return fetchAPI<Gift[]>('/api/orders/gifts/');
  },
  
  async createOrder(params: {
    project_ids: string[]
    shipping_name?: string
    shipping_address_line1: string
    shipping_address_line2?: string
    shipping_city: string
    shipping_state?: string
    shipping_postal_code: string
    shipping_country: string
    billing_same_as_shipping?: boolean
    billing_name?: string
    billing_address_line1?: string
    billing_address_line2?: string
    billing_city?: string
    billing_state?: string
    billing_postal_code?: string
    billing_country?: string
    shipping_method?: string
    customer_notes?: string
    coupon_code?: string
    extras?: Array<{ extra_id: string, quantity: number }>
    gifts?: string[]
    tracking?: {
      purchase_event_id?: string
      ga_client_id?: string
      gcl_au?: string
      fbp?: string
      fbc?: string
      external_id?: string
    }
  }): Promise<Order> {
    return fetchAPI<Order>('/api/orders/', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        billing_same_as_shipping: params.billing_same_as_shipping ?? true,
        shipping_method: params.shipping_method || 'STANDARD',
      }),
    });
  },
  
  async initiateCheckout(orderId: string, paymentMethod: 'stripe' | 'paypal'): Promise<{
    payment_method: string
    session_id?: string
    checkout_url?: string
    paypal_order_id?: string
    approval_url?: string
    order_id: string
    order_number: string
    total: string
    currency: string
    is_retry: boolean
  }> {
    return fetchAPI(`/api/orders/${orderId}/checkout/`, {
      method: 'POST',
      body: JSON.stringify({ payment_method: paymentMethod }),
    });
  },
  
  async capturePayPalPayment(orderId: string, paypalOrderId: string): Promise<{
    status: string
    order_status: string
    order_id: string
    order_number: string
    capture_id: string
  }> {
    return fetchAPI(`/api/orders/${orderId}/paypal-capture/`, {
      method: 'POST',
      body: JSON.stringify({ paypal_order_id: paypalOrderId }),
    });
  },
  
  async getList(): Promise<Order[]> {
    return fetchAPI<Order[]>('/api/orders/');
  },
  
  async get(id: string): Promise<Order> {
    return fetchAPI<Order>(`/api/orders/${id}/`);
  },
};

/**
 * AI Proxies API
 */
export const aiProxiesAPI = {
  async generate(params: any): Promise<any> {
    return fetchAPI<any>('/api/ai-proxies/generate/', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        frame_version: params.frame_version || 'ART_ONLY',
        confirmed: params.confirmed ?? true,
      }),
    });
  },

  async getJobStatus(jobId: string): Promise<any> {
    return fetchAPI<any>(`/api/ai-proxies/jobs/${jobId}/status/`);
  },

  async getJobResult(jobId: string): Promise<any> {
    return fetchAPI<any>(`/api/ai-proxies/jobs/${jobId}/`);
  },

  async getBalance(): Promise<any> {
    return fetchAPI<any>('/api/ai-proxies/credits/balance/');
  },

  async getPackages(): Promise<any> {
    return fetchAPI<any>('/api/ai-proxies/credits/packages/');
  },
};

/**
 * Shop Integration API
 */
export const shopAPI = {
  getOrderUrl(cardId: string): string {
    return `${DJANGO_API_URL}/order?card_id=${cardId}`;
  },
  
  async addToCart(cardId: string, quantity: number = 1): Promise<any> {
    return fetchAPI('/shop/cart/add/', {
      method: 'POST',
      body: JSON.stringify({ card_id: cardId, quantity }),
    });
  },
};

/**
 * Gamification API
 */
export const gamificationAPI = {
  async getProfile(): Promise<GamificationProfile> {
    return fetchAPI<GamificationProfile>('/api/gamification/profile/');
  },

  async getQuests(): Promise<QuestsResponse> {
    return fetchAPI<QuestsResponse>('/api/gamification/quests/');
  },

  async claimQuest(questId: string): Promise<ClaimQuestResponse> {
    return fetchAPI<ClaimQuestResponse>(`/api/gamification/quests/${questId}/claim/`, {
      method: 'POST',
    });
  },

  async getBattlePass(): Promise<any | null> {
    try {
      return await fetchAPI<any>('/api/gamification/battle-pass/');
    } catch (e: any) {
      if (e?.message?.includes('204') || e?.message?.includes('Request failed')) {
        return null;
      }
      throw e;
    }
  },

  async claimStep(stepNumber: number): Promise<any> {
    return fetchAPI<any>(`/api/gamification/battle-pass/steps/${stepNumber}/claim/`, {
      method: 'POST',
    });
  },

  async claimBonus(): Promise<any> {
    return fetchAPI<any>('/api/gamification/battle-pass/bonus/claim/', {
      method: 'POST',
    });
  },
};

// ============================================================
// Gamification Types
// ============================================================

export interface GamificationLevelInfo {
  level: number
  title: string
  xp_current_level: number
  xp_next_level: number | null
  progress: number // 0.0–1.0 within current level band
}

export interface GamificationProfile {
  xp_total: number
  xp_balance: number
  level: number
  level_title: string
  level_info: GamificationLevelInfo
  login_streak: number
  longest_streak: number
  last_login_date: string
  streak_multiplier: number
  spark: number
  spark_lifetime: number
  created_at: string
  updated_at: string
}

export interface QuestReward {
  id: string
  reward_type: string
  reward_type_display: string
  name: string
  description: string
  amount: string | null
  ref: string
  icon: string
}

export interface QuestDefinition {
  id: string
  title: string
  description: string
  icon: string
  trigger_event: string
  required_count: number
  pool_type: 'DAILY' | 'WEEKLY'
  pool_label: string
  difficulty: 'EASY' | 'MEDIUM' | 'HARD'
  difficulty_label: string
  rewards: QuestReward[]
}

export interface UserQuest {
  id: string
  quest: QuestDefinition
  period_key: string
  pool_type: 'DAILY' | 'WEEKLY'
  progress: number
  status: 'IN_PROGRESS' | 'COMPLETED' | 'CLAIMED' | 'EXPIRED'
  status_display: string
  is_complete: boolean
  completed_at: string | null
  claimed_at: string | null
  created_at: string
}

export interface QuestsResponse {
  daily: UserQuest[]
  weekly: UserQuest[]
}

export interface ClaimQuestResponse {
  message: string
  xp_bonus: number
  rewards_granted: number
}
